import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon, LoaderIcon, PlusIcon, TrashIcon } from '@/components/icons';
import { sandboxApi } from '@/api/sandbox.api';
import { sandboxConfigApi } from '@/api/sandboxConfig.api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableRow, TableCell } from '@/components/ui/table';
import { create } from '@bufbuild/protobuf';
import { SandboxSchema, SandboxType } from '@/proto/agentplatform/v1/sandbox_pb';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import type { SandboxSpec, SandboxEnvironment, Sandbox } from '@/proto/agentplatform/v1/sandbox_pb';
import { secretApi } from '@/api/secret.api';
import { fullErrorText } from '@/api/errors';
import { buildSecret } from '@/lib/secretStorage';
import StorageMountSection from './StorageMountSection';
import {
  emptyMount,
  fromMountPoints,
  toMountPoints,
  validateStorageMounts,
  validateMountCredential,
  saveErrorMessage,
  type MountDraft,
} from './storageMount';

interface EnvEntry {
  key: string;
  value: string;
}

interface FormState {
  name: string;
  description: string;
  selectedSpecId: string;
  selectedEnvironmentId: string;
  envVars: EnvEntry[];
  selectedSandboxType: SandboxType;
  storageEnabled: boolean;
  storageMounts: MountDraft[];
}

const SECTIONS = [
  { id: 'basic', label: '基本信息' },
  { id: 'sandbox-type', label: '沙箱类型' },
  { id: 'spec', label: '资源规格' },
  { id: 'env', label: '环境配置' },
  { id: 'vars', label: '环境变量' },
  { id: 'storage', label: '存储挂载' },
];

function IconDocument() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function IconCpu() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
      />
    </svg>
  );
}

function IconServer() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
      />
    </svg>
  );
}

function IconVariable() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.871 4A17.926 17.926 0 003 12c0 2.874.673 5.59 1.871 8m14.13 0a17.926 17.926 0 001.87-8c0-2.874-.673-5.59-1.87-8M9 9h1.246a1 1 0 01.961.725l1.586 5.55a1 1 0 00.961.725H15m1-7h-.08a2 2 0 00-1.519.698L9.6 15.302A2 2 0 018.08 16H8"
      />
    </svg>
  );
}

function RadioIndicator({ selected }: { selected: boolean }) {
  return (
    <div
      className={`flex h-4 w-4 items-center justify-center rounded-full border-2 transition-colors ${selected ? 'border-primary bg-primary' : 'border-muted-foreground/25'}`}
    >
      {selected && <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />}
    </div>
  );
}

function SectionCard({
  id,
  icon,
  title,
  subtitle,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
        <div className="h-0.5 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
              {icon}
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">{title}</h2>
              {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

export default function CreateSandboxInstancePage() {
  const { id, uid } = useParams<{ id?: string; uid?: string }>();
  const instanceId = id || uid;
  const navigate = useNavigate();
  const isEditMode = !!instanceId;
  const [activeSection, setActiveSection] = useState('basic');
  const [showValidation, setShowValidation] = useState(false);
  const [storageError, setStorageError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  const [form, setForm] = useState<FormState>({
    name: '',
    description: '',
    selectedSpecId: '',
    selectedEnvironmentId: '',
    envVars: [],
    selectedSandboxType: SandboxType.DOCKER,
    storageEnabled: false,
    storageMounts: [emptyMount()],
  });

  const { data: instanceData, isLoading: instanceLoading } = useQuery({
    queryKey: ['sandbox', instanceId],
    queryFn: () => sandboxApi.getSandbox(instanceId!),
    enabled: isEditMode && !!instanceId,
  });

  const { data: specsData, isLoading: specsLoading } = useQuery({
    queryKey: ['sandbox-specs'],
    queryFn: () =>
      sandboxConfigApi.listSandboxSpecs(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const { data: envsData, isLoading: envsLoading } = useQuery({
    queryKey: ['sandbox-environments'],
    queryFn: () =>
      sandboxConfigApi.listSandboxEnvironments(
        create(ListRequestSchema, { page: 1, pageSize: 100 })
      ),
  });

  const allSpecs: SandboxSpec[] = specsData?.data ?? [];
  const specs: SandboxSpec[] = allSpecs.filter(
    (s) => (s.specConfig?.sandboxType ?? SandboxType.DOCKER) === form.selectedSandboxType
  );
  const envs: SandboxEnvironment[] = envsData?.data ?? [];

  useEffect(() => {
    if (instanceData) {
      const envVars: EnvEntry[] = [];
      if (instanceData.env) {
        Object.entries(instanceData.env).forEach(([k, v]) => {
          envVars.push({ key: k, value: String(v) });
        });
      }
      const mounts = fromMountPoints(instanceData.mountPoints ?? []);
      const hasMounts = (instanceData.mountPoints ?? []).length > 0;
      setForm({
        name: instanceData.name || '',
        description: instanceData.description || '',
        selectedSpecId: instanceData.sandboxSpecId || '',
        selectedEnvironmentId: instanceData.sandboxEnvironmentId || '',
        envVars,
        selectedSandboxType: instanceData.type === 'vm' ? SandboxType.VM : SandboxType.DOCKER,
        storageEnabled: hasMounts,
        storageMounts: mounts,
      });
    }
  }, [instanceData]);

  const createMutation = useMutation({
    mutationFn: (data: Sandbox) => sandboxApi.createSandbox(data),
    onSuccess: () => navigate('/plugins/sandboxes?tab=instance'),
    onError: (error) => setStorageError(saveErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Sandbox) => sandboxApi.updateSandbox(instanceId!, data),
    onSuccess: () => navigate(`/plugins/sandboxes/instances/${instanceId}`),
    onError: (error) => setStorageError(saveErrorMessage(error)),
  });

  const isPending = createMutation.isPending || updateMutation.isPending || submitting;
  const setField = (field: 'name' | 'description', value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const resolveInlineMounts = async (): Promise<MountDraft[]> => {
    const resolved = await Promise.all(
      form.storageMounts.map(async (m) => {
        if (m.credentialSource !== 'inline') return m;
        const c = m.inlineCredential;
        const secretId = await secretApi.createSecretReturningId(
          buildSecret({
            name: c.name.trim(),
            storageType: c.storageType,
            region: c.region.trim(),
            endpoint: c.endpoint.trim(),
            accessKey: c.accessKey.trim(),
            secretKey: c.secretKey,
          })
        );
        return { ...m, secretId };
      })
    );
    return resolved;
  };

  const handleSubmit = async () => {
    setShowValidation(true);
    setStorageError('');
    if (!form.name) return;
    if (!form.selectedSpecId || !form.selectedEnvironmentId) return;
    const envMap: Record<string, string> = {};
    form.envVars.forEach((e) => {
      if (e.key) envMap[e.key] = e.value;
    });

    let mountPoints: Sandbox['mountPoints'] = [];
    if (form.storageEnabled) {
      const preValidate = validateStorageMounts(form.storageMounts);
      if (preValidate) {
        setStorageError(preValidate.reason);
        return;
      }
      setSubmitting(true);
      try {
        await validateMountCredential(form.storageMounts);
      } catch (e) {
        setSubmitting(false);
        setStorageError(`凭证校验失败：${fullErrorText(e)}`);
        return;
      }
      let resolvedMounts: MountDraft[];
      try {
        resolvedMounts = await resolveInlineMounts();
      } catch (e) {
        setSubmitting(false);
        setStorageError(`创建凭证失败：${fullErrorText(e)}`);
        return;
      }
      setSubmitting(false);
      mountPoints = toMountPoints(resolvedMounts);
    }

    const payload = create(SandboxSchema, {
      name: form.name,
      description: form.description,
      type: form.selectedSandboxType === SandboxType.VM ? 'vm' : 'docker',
      sandboxSpecId: form.selectedSpecId,
      sandboxEnvironmentId: form.selectedEnvironmentId,
      env: envMap,
      mountPoints,
    });

    if (isEditMode && instanceId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="w-full px-6 pt-8 pb-4">
        <button
          onClick={() => navigate('/plugins/sandboxes')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          返回沙箱管理
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              {isEditMode ? '编辑沙箱实例' : '新建沙箱实例'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isEditMode ? '修改沙箱实例的配置信息' : '选择预定义规格和环境创建沙箱实例'}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-6 pb-16">
        <div className="flex gap-6 items-start">
          <div className="flex-1 min-w-0 space-y-5">
            <SectionCard
              id="basic"
              icon={<IconDocument />}
              title="基本信息"
              subtitle="沙箱实例的名称和描述"
            >
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-sm font-medium">
                    实例名称 <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="例如: production-cache、staging-worker-01"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    className="h-10"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="desc" className="text-sm font-medium">
                    描述
                  </Label>
                  <textarea
                    id="desc"
                    placeholder="简要描述此沙箱实例的用途..."
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                    rows={3}
                    className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              id="sandbox-type"
              icon={<IconServer />}
              title="沙箱类型"
              subtitle="选择 Docker 容器或虚拟机沙箱类型"
            >
              <div className="flex w-full overflow-hidden rounded-xl bg-muted/40 p-1.5 ring-1 ring-inset ring-border/50">
                <div
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      selectedSandboxType: SandboxType.DOCKER,
                      selectedSpecId: '',
                    }))
                  }
                  className={`relative flex w-1/2 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg py-3.5 text-center transition-all duration-200 ${
                    form.selectedSandboxType === SandboxType.DOCKER
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                  }`}
                >
                  <div
                    className={`transition-transform duration-200 ${form.selectedSandboxType === SandboxType.DOCKER ? 'scale-110 text-blue-500' : 'text-muted-foreground/70'}`}
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold tracking-tight">Docker 容器</span>
                    {form.selectedSandboxType === SandboxType.DOCKER && (
                      <span className="text-[10px] leading-tight text-muted-foreground/80 mt-0.5 animate-in fade-in slide-in-from-bottom-1">
                        轻量、安全、可移植
                      </span>
                    )}
                  </div>
                </div>
                <div
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      selectedSandboxType: SandboxType.VM,
                      selectedSpecId: '',
                    }))
                  }
                  className={`relative flex w-1/2 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg py-3.5 text-center transition-all duration-200 ${
                    form.selectedSandboxType === SandboxType.VM
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                  }`}
                >
                  <div
                    className={`transition-transform duration-200 ${form.selectedSandboxType === SandboxType.VM ? 'scale-110 text-purple-500' : 'text-muted-foreground/70'}`}
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                      />
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold tracking-tight">Virtual Machine</span>
                    {form.selectedSandboxType === SandboxType.VM && (
                      <span className="text-[10px] leading-tight text-muted-foreground/80 mt-0.5 animate-in fade-in slide-in-from-bottom-1">
                        更强的隔离环境
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              id="spec"
              icon={<IconCpu />}
              title={
                <>
                  资源规格 <span className="text-destructive">*</span>
                </>
              }
              subtitle="选择一个资源规格，确定沙箱的 CPU、内存、存储配置"
            >
              {showValidation && !form.selectedSpecId && (
                <p className="text-xs text-destructive mb-2">请选择一个资源规格</p>
              )}
              <div className="max-h-64 overflow-auto -mx-6 -mb-6">
                <Table>
                  <TableBody>
                    {specsLoading ? (
                      [1, 2, 3].map((i) => (
                        <TableRow key={i}>
                          <TableCell className="w-12" />
                          <TableCell>
                            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                          </TableCell>
                          <TableCell>
                            <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                          </TableCell>
                          <TableCell>
                            <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : specs.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center py-12 text-muted-foreground text-sm"
                        >
                          暂无可用资源规格
                        </TableCell>
                      </TableRow>
                    ) : (
                      specs.map((spec) => {
                        const selected = form.selectedSpecId === spec.id;
                        const cpu = spec.specConfig?.cpuMilliCores
                          ? `${(spec.specConfig.cpuMilliCores / 1000).toFixed(1)} 核`
                          : '-';
                        const memory = spec.specConfig?.memoryMb
                          ? spec.specConfig.memoryMb >= 1000
                            ? `${(spec.specConfig.memoryMb / 1000).toFixed(1)} GB`
                            : `${spec.specConfig.memoryMb} MB`
                          : '-';
                        return (
                          <TableRow
                            key={spec.id}
                            className="cursor-pointer"
                            data-state={selected ? 'selected' : undefined}
                            onClick={() =>
                              setForm((prev) => ({ ...prev, selectedSpecId: spec.id }))
                            }
                          >
                            <TableCell className="w-12 pl-6">
                              <RadioIndicator selected={selected} />
                            </TableCell>
                            <TableCell className="py-3">
                              <span className="font-medium text-sm">{spec.name}</span>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="flex flex-col gap-0.5">
                                <span className="text-xs text-muted-foreground">CPU: {cpu}</span>
                                <span className="text-xs text-muted-foreground">
                                  Memory: {memory}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="py-3 pr-6">
                              {spec.tags.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {spec.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="inline-flex items-center rounded-none bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground ring-1 ring-inset ring-border/40"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>

            <SectionCard
              id="env"
              icon={<IconServer />}
              title={
                <>
                  环境配置 <span className="text-destructive">*</span>
                </>
              }
              subtitle="选择一个环境配置，确定沙箱的镜像、工作目录等环境信息"
            >
              {showValidation && !form.selectedEnvironmentId && (
                <p className="text-xs text-destructive mb-2">请选择一个环境配置</p>
              )}
              <div className="max-h-64 overflow-auto -mx-6 -mb-6">
                <Table>
                  <TableBody>
                    {envsLoading ? (
                      [1, 2, 3].map((i) => (
                        <TableRow key={i}>
                          <TableCell className="w-12" />
                          <TableCell>
                            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                          </TableCell>
                          <TableCell>
                            <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : envs.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-center py-12 text-muted-foreground text-sm"
                        >
                          暂无可用环境配置
                        </TableCell>
                      </TableRow>
                    ) : (
                      envs.map((env) => {
                        const selected = form.selectedEnvironmentId === env.id;
                        return (
                          <TableRow
                            key={env.id}
                            className="cursor-pointer"
                            data-state={selected ? 'selected' : undefined}
                            onClick={() =>
                              setForm((prev) => ({ ...prev, selectedEnvironmentId: env.id }))
                            }
                          >
                            <TableCell className="w-12 pl-6">
                              <RadioIndicator selected={selected} />
                            </TableCell>
                            <TableCell className="py-3">
                              <span className="font-medium text-sm">{env.name}</span>
                            </TableCell>
                            <TableCell className="py-3 pr-6">
                              {env.tags.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {env.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="inline-flex items-center rounded-none bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground ring-1 ring-inset ring-border/40"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>

            <SectionCard
              id="vars"
              icon={<IconVariable />}
              title="环境变量"
              subtitle="配置容器运行时环境变量"
            >
              <div className="space-y-3">
                {form.envVars.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 px-4 rounded-lg border-2 border-dashed border-border/60 bg-muted/20">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
                      <IconVariable />
                    </div>
                    <span className="text-sm text-muted-foreground mb-1">暂无环境变量</span>
                    <span className="text-xs text-muted-foreground/70">点击下方按钮添加</span>
                  </div>
                )}
                {form.envVars.map((entry, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-muted/20 group"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Input
                        placeholder="KEY"
                        value={entry.key}
                        onChange={(e) => {
                          const next = [...form.envVars];
                          next[i] = { ...next[i], key: e.target.value };
                          setForm((prev) => ({ ...prev, envVars: next }));
                        }}
                        className="h-9 w-40 font-mono text-xs shrink-0"
                      />
                      <span className="text-muted-foreground shrink-0">=</span>
                      <Input
                        placeholder="VALUE"
                        value={entry.value}
                        onChange={(e) => {
                          const next = [...form.envVars];
                          next[i] = { ...next[i], value: e.target.value };
                          setForm((prev) => ({ ...prev, envVars: next }));
                        }}
                        className="h-9 flex-1 font-mono text-xs"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          envVars: prev.envVars.filter((_, j) => j !== i),
                        }))
                      }
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      envVars: [...prev.envVars, { key: '', value: '' }],
                    }))
                  }
                >
                  <PlusIcon className="h-3.5 w-3.5 mr-1" />
                  添加环境变量
                </Button>
              </div>
            </SectionCard>

            <StorageMountSection
              enabled={form.storageEnabled}
              onEnabledChange={(v) => setForm((prev) => ({ ...prev, storageEnabled: v }))}
              mounts={form.storageMounts}
              onMountsChange={(v) => setForm((prev) => ({ ...prev, storageMounts: v }))}
            />
            {storageError && <p className="text-xs text-destructive -mt-2 px-1">{storageError}</p>}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/plugins/sandboxes')}
                className="h-10 px-6"
              >
                取消
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  isPending ||
                  instanceLoading ||
                  !form.name.trim() ||
                  !form.selectedSpecId ||
                  !form.selectedEnvironmentId
                }
                className="h-10 px-8 gap-2"
              >
                {isPending ? (
                  <>
                    <LoaderIcon className="h-4 w-4 animate-spin" />
                    保存中...
                  </>
                ) : isEditMode ? (
                  '保存修改'
                ) : (
                  '创建沙箱实例'
                )}
              </Button>
            </div>
          </div>

          <div className="w-12 shrink-0 self-start sticky top-6">
            <div className="flex flex-col items-center gap-3 py-4">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(s.id)}
                  title={s.label}
                  className="group relative flex items-center"
                >
                  <span
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      activeSection === s.id
                        ? 'w-3 h-3 bg-primary shadow-sm shadow-primary/50'
                        : 'bg-border hover:bg-muted-foreground/60'
                    }`}
                  />
                  <span className="absolute right-full mr-3 px-2 py-1 rounded bg-foreground/90 text-background text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity font-medium">
                    {s.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
